import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import { useNotifications } from "../../helpers/getNotifications";
import { StatusBadge, normalizeStatus } from "../../components/inpi/StatusBadge";
import { TipoIcon, tipoConfig } from "../../components/inpi/TipoIcon";
import api from "../../services/api/api";
import {
  FaTag,
  FaCode,
  FaGlobeAmericas,
  FaLightbulb,
  FaClock,
  FaExternalLinkAlt,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaList,
  FaFileAlt,
  FaShieldAlt,
  FaTrash,
} from "react-icons/fa";
import { Button, Dropdown, Form, Input, Modal, Popup, Select, Table } from "semantic-ui-react";
import "./RegistrosINPI.css";

const PROCESS_TYPES = ["marca", "patente", "software", "marca_internacional"];

function formatDateBR(value) {
  if (!value) return "—";
  const str = String(value);
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return str;
}

function formatProcessNumberBR(num) {
  if (!num) return num;
  const n = String(num).replace(/\D/g, "");
  if (n.length < 13) return num;
  return `BR ${n.slice(0, 2)} ${n.slice(2, 6)} ${n.slice(6, 12)} ${n.slice(12)}`;
}

function formatComplemento(text) {
  if (!text || typeof text !== "string") return text;
  return text
    .replace(/(Protocolo|Petição\s*\(tipo\)|Petição|Requerente|Procurador|Detalhes do despacho)\s*:/gi, "\n$1: ")
    .replace(/^\s*\n+/, "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .join("\n")
    .trim();
}

const ULTIMA_MOV_MAX_LENGTH = 55;

function truncateMovimentacao(text) {
  if (!text || text.length <= ULTIMA_MOV_MAX_LENGTH) return text || "";
  return text.slice(0, ULTIMA_MOV_MAX_LENGTH).trim() + "...";
}

function mapProcessToRegistro(proc) {
  const tipo = PROCESS_TYPES.includes(proc.processType) ? proc.processType : "marca";
  const statusKey = normalizeStatus(proc.status);
  const lastMov = proc.lastMovement;
  const ultimaMovimentacao = lastMov
    ? `${lastMov.description || ""}${lastMov.date ? ` — ${formatDateBR(lastMov.date)}` : ""}`.trim()
    : "Sem movimentação";
  const dataAbertura = proc.depositDate || proc.publicationDate;
  let descricao = proc.statusDetail || "—";
  if (descricao === "—" && proc.classNumbers && proc.classNumbers.length)
    descricao = `Classes: ${proc.classNumbers.join(", ")}`;

  return {
    id: proc._id,
    cliente: proc.clientName || "—",
    nome: proc.trademarkName || "—",
    protocolo: proc.processNumber || "—",
    numeroServico: null,
    tipo,
    status: statusKey,
    ultimaMovimentacao,
    dataAbertura: dataAbertura ? formatDateBR(dataAbertura) : "—",
    descricao,
  };
}

const acoesSolicitacao = [
  { tipo: "marca", label: "Registrar Marca", descricao: "Nacional — Classes INPI", icon: FaTag },
  { tipo: "software", label: "Registro de Software", descricao: "Programa de computador", icon: FaCode },
  { tipo: "marca_internacional", label: "Marca Internacional", descricao: "Protocolo de Madrid", icon: FaGlobeAmericas },
  { tipo: "patente", label: "Patente", descricao: "Invenção ou modelo de utilidade", icon: FaLightbulb },
];

function ClienteAvatar({ nome }) {
  const initials = nome
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();
  const colors = ["inpi-avatar--blue", "inpi-avatar--green", "inpi-avatar--purple", "inpi-avatar--orange", "inpi-avatar--navy"];
  const colorIdx = nome.charCodeAt(0) % colors.length;
  return <span className={`inpi-avatar ${colors[colorIdx]}`}>{initials}</span>;
}

const statusOptions = [
  { key: "todos", value: "todos", text: "Todos os status" },
  { key: "pedido_protocolado", value: "pedido_protocolado", text: "Pedido protocolado" },
  { key: "exame_formal", value: "exame_formal", text: "Exame formal" },
  { key: "aguardando_oposicao", value: "aguardando_oposicao", text: "Aguard. oposição" },
  { key: "aguardando_manifestacao", value: "aguardando_manifestacao", text: "Aguard. manifestação" },
  { key: "aguardando_merito", value: "aguardando_merito", text: "Aguard. mérito" },
  { key: "deferido", value: "deferido", text: "Deferido" },
  { key: "indeferido", value: "indeferido", text: "Indeferido" },
  { key: "registro_ativo", value: "registro_ativo", text: "Registro ativo" },
  { key: "arquivado", value: "arquivado", text: "Arquivado" },
];

const tipoOptions = [
  { key: "todos", value: "todos", text: "Todos os tipos" },
  { key: "marca", value: "marca", text: "Registro de Marca" },
  { key: "software", value: "software", text: "Registro de Software" },
  { key: "marca_internacional", value: "marca_internacional", text: "Marca Internacional" },
  { key: "patente", value: "patente", text: "Patente" },
];

const RegistrosINPI = () => {
  const { notifications, checkNotification } = useNotifications();
  const [sidebarIsOpen, setSidebarIsOpen] = useState(true);
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [dialogCadastrarAberto, setDialogCadastrarAberto] = useState(false);
  const [selectedRegistro, setSelectedRegistro] = useState(null);
  const [dialogDetalheAberto, setDialogDetalheAberto] = useState(false);
  const [processDetail, setProcessDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detalheSections, setDetalheSections] = useState({
    classificacao: true,
    peticoes: true,
    publicacoes: true,
  });

  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage] = useState(15);
  const [totais, setTotais] = useState({ ativos: 0, pendentes: 0, deferidos: 0, indeferidos: 0, outros: 0 });
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [formClientId, setFormClientId] = useState("");
  const [formClientLabel, setFormClientLabel] = useState("");
  const [formProcessNumber, setFormProcessNumber] = useState("");
  const [formProcessType, setFormProcessType] = useState("marca");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const prevFiltersRef = useRef({ search: "", status: "todos", type: "todos" });
  const clientSearchDebounceRef = useRef(null);
  const clientOptionsRef = useRef([]);
  const CLIENT_PAGE_SIZE = 25;

  useEffect(() => {
    clientOptionsRef.current = clientOptions;
  }, [clientOptions]);

  const fetchProcesses = useCallback((page, search, status, processType) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status !== "todos") params.set("status", status);
    if (processType !== "todos") params.set("processType", processType);
    params.set("page", String(page));
    params.set("per_page", String(perPage));
    api
      .get(`/inpi/processes?${params.toString()}`)
      .then((res) => {
        const data = res.data?.data ?? [];
        setProcessos(data);
        setTotal(res.data?.total ?? 0);
        setCurrentPage(res.data?.current_page ?? 1);
        setLastPage(res.data?.last_page ?? 1);
        setTotais(res.data?.totals ?? { ativos: 0, pendentes: 0, deferidos: 0, indeferidos: 0, outros: 0 });
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Erro ao carregar registros INPI");
        setProcessos([]);
        setTotal(0);
        setTotais({ ativos: 0, pendentes: 0, deferidos: 0, indeferidos: 0, outros: 0 });
      })
      .finally(() => setLoading(false));
  }, [perPage]);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(busca), 400);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filterChanged = prev.search !== buscaDebounced || prev.status !== filtroStatus || prev.type !== filtroTipo;
    prevFiltersRef.current = { search: buscaDebounced, status: filtroStatus, type: filtroTipo };
    const pageToFetch = filterChanged ? 1 : currentPage;
    if (filterChanged) setCurrentPage(1);
    fetchProcesses(pageToFetch, buscaDebounced, filtroStatus, filtroTipo);
  }, [currentPage, buscaDebounced, filtroStatus, filtroTipo, fetchProcesses]);

  const fetchClients = useCallback((search = "", append = false) => {
    setClientSearchLoading(true);
    const params = new URLSearchParams();
    params.set("page", "1");
    params.set("per_page", String(CLIENT_PAGE_SIZE));
    if (search) params.set("search", search);
    api
      .get(`/clients/index?${params.toString()}`)
      .then((res) => {
        const data = res.data?.data || [];
        const opts = data.map((c) => {
          const id = typeof c._id === "string" ? c._id : c._id?.$oid || c._id;
          return {
            key: id,
            value: id,
            text: c.companyName || c.fullName || id,
          };
        });
        setClientOptions(append ? (prev) => [...prev, ...opts] : opts);
      })
      .catch(() => setClientOptions([]))
      .finally(() => setClientSearchLoading(false));
  }, []);

  useEffect(() => {
    if (!dialogCadastrarAberto) {
      setClientOptions([]);
      setClientSearchQuery("");
      setFormClientLabel("");
      return;
    }
  }, [dialogCadastrarAberto]);

  useEffect(() => {
    if (!dialogDetalheAberto || !selectedRegistro?.id) {
      setProcessDetail(null);
      return;
    }
    setLoadingDetail(true);
    setProcessDetail(null);
    api
      .get(`/inpi/processes/${selectedRegistro.id}`)
      .then((res) => setProcessDetail(res.data))
      .catch(() => setProcessDetail(null))
      .finally(() => setLoadingDetail(false));
  }, [dialogDetalheAberto, selectedRegistro?.id]);

  const toggleDetalheSection = (key) => {
    setDetalheSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClientSearchChange = useCallback((e, { searchQuery }) => {
    setClientSearchQuery(searchQuery);
    if (clientSearchDebounceRef.current) clearTimeout(clientSearchDebounceRef.current);
    clientSearchDebounceRef.current = setTimeout(() => {
      fetchClients(searchQuery);
      clientSearchDebounceRef.current = null;
    }, 350);
  }, [fetchClients]);

  const handleClientDropdownOpen = useCallback(() => {
    if (clientOptions.length === 0 && !clientSearchLoading) fetchClients(clientSearchQuery || "");
  }, [clientOptions.length, clientSearchLoading, clientSearchQuery, fetchClients]);

  const handleClientChange = useCallback((e, { value }) => {
    setFormClientId(value || "");
    const opt = clientOptionsRef.current.find((o) => o.value === value);
    setFormClientLabel(opt ? opt.text : "");
    setClientSearchQuery("");
  }, []);

  const registros = processos.map(mapProcessToRegistro);

  const handlePageChange = (nextPage) => {
    if (nextPage >= 1 && nextPage <= lastPage) setCurrentPage(nextPage);
  };

  const handleSidebar = (open) => setSidebarIsOpen(open);

  const openCadastrarModal = (tipo = "marca") => {
    setDialogCadastrarAberto(true);
    setFormClientId("");
    setFormClientLabel("");
    setFormProcessNumber("");
    setFormProcessType(PROCESS_TYPES.includes(tipo) ? tipo : "marca");
    setSubmitError(null);
    setClientOptions([]);
    setClientSearchQuery("");
  };

  const handleSubmitCadastrar = () => {
    if (!formClientId || !formProcessNumber || !formProcessType) {
      setSubmitError("Preencha cliente, número do processo e tipo.");
      return;
    }
    setSubmitLoading(true);
    setSubmitError(null);
    api
      .post("/inpi/processes", {
        clientId: formClientId,
        processNumber: formProcessNumber.trim(),
        processType: formProcessType,
      })
      .then(() => {
        setDialogCadastrarAberto(false);
        fetchProcesses(1, buscaDebounced, filtroStatus, filtroTipo);
        alert("Processo cadastrado com sucesso.");
      })
      .catch((err) => {
        setSubmitError(err.response?.data?.message || "Erro ao cadastrar processo.");
      })
      .finally(() => setSubmitLoading(false));
  };

  const handleApagarProcesso = () => {
    if (!selectedRegistro?.id) return;
    if (!window.confirm("Tem certeza que deseja apagar este processo? Esta ação não pode ser desfeita.")) return;
    setDeleteLoading(true);
    api
      .delete(`/inpi/processes/${selectedRegistro.id}`)
      .then(() => {
        setDialogDetalheAberto(false);
        setSelectedRegistro(null);
        fetchProcesses(currentPage, buscaDebounced, filtroStatus, filtroTipo);
        alert("Processo removido com sucesso.");
      })
      .catch((err) => {
        alert(err.response?.data?.message || "Erro ao remover processo.");
      })
      .finally(() => setDeleteLoading(false));
  };

  return (
    <div id="registros-inpi">
      <div className="row">
        <div className="col s12">
          <Sidebar
            isOpen={handleSidebar}
            checkNotification={checkNotification}
            notifications={notifications || []}
          />
        </div>
      </div>
      <div className={!sidebarIsOpen ? "container-width" : "container-width-sbopen"}>
        <div className="registros-inpi-page">
          <div className="registros-inpi-header">
            <div>
              <div className="registros-inpi-header-title">
                <FaShieldAlt className="registros-inpi-header-icon" />
                <span>INPI — Área do Advogado</span>
              </div>
              <h1>Registros INPI</h1>
              <p className="registros-inpi-header-desc">Todos os pedidos de propriedade intelectual dos seus clientes</p>
            </div>
          </div>

          <div className="registros-inpi-content">
            <div className="registros-inpi-cards">
              <div className="registros-inpi-card">
                <p className="registros-inpi-card-label">Em andamento</p>
                <p className="registros-inpi-card-value andamento">{totais.pendentes}</p>
                <p className="registros-inpi-card-sub">de {total} registros</p>
              </div>
              <div className="registros-inpi-card">
                <p className="registros-inpi-card-label">Deferidos / Registros ativos</p>
                <p className="registros-inpi-card-value deferido">{totais.deferidos}</p>
                <p className="registros-inpi-card-sub">de {total} registros</p>
              </div>
              <div className="registros-inpi-card">
                <p className="registros-inpi-card-label">Indeferidos / Arquivados</p>
                <p className="registros-inpi-card-value indeferido">{totais.indeferidos}</p>
                <p className="registros-inpi-card-sub">de {total} registros</p>
              </div>
              {totais.outros > 0 && (
                <div className="registros-inpi-card">
                  <p className="registros-inpi-card-label">Outros</p>
                  <p className="registros-inpi-card-value">{totais.outros}</p>
                  <p className="registros-inpi-card-sub">de {total} registros</p>
                </div>
              )}
            </div>

            <section className="registros-inpi-section">
              <h2 className="registros-inpi-section-title">Solicitar novo registro</h2>
              <div className="registros-inpi-acoes">
                {acoesSolicitacao.map((acao) => {
                  const Icon = acao.icon;
                  const config = tipoConfig[acao.tipo];
                  return (
                    <button
                      key={acao.tipo}
                      type="button"
                      className="registros-inpi-acao-card"
                      onClick={() => openCadastrarModal(acao.tipo)}
                    >
                      <span className={`registros-inpi-acao-icon ${config ? config.className : ""}`}>
                        <Icon size={14} />
                      </span>
                      <div>
                        <p className="registros-inpi-acao-label">{acao.label}</p>
                        <p className="registros-inpi-acao-desc">{acao.descricao}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="registros-inpi-filtros">
              <Form className="registros-inpi-filtros-form">
                <Form.Group className="registros-inpi-filtros-group" inline>
                  <Form.Field className="registros-inpi-field-search">
                    <Input
                      icon="search"
                      placeholder="Buscar por cliente, nome ou protocolo..."
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      className="registros-inpi-search-input"
                    />
                  </Form.Field>
                  <Form.Field className="registros-inpi-field-status">
                    <Dropdown
                      selection
                      options={statusOptions}
                      value={filtroStatus}
                      onChange={(e, { value }) => setFiltroStatus(value ?? "todos")}
                      placeholder="Status"
                      className="registros-inpi-select"
                    />
                  </Form.Field>
                  <Form.Field className="registros-inpi-field-tipo">
                    <Dropdown
                      selection
                      options={tipoOptions}
                      value={filtroTipo}
                      onChange={(e, { value }) => setFiltroTipo(value ?? "todos")}
                      placeholder="Tipo"
                      className="registros-inpi-select"
                    />
                  </Form.Field>
                  <Form.Field className="registros-inpi-field-count">
                    <span className="registros-inpi-count">
                      {total === 0
                        ? "0 registros"
                        : `${(currentPage - 1) * perPage + 1}-${Math.min(currentPage * perPage, total)} de ${total} registro${total !== 1 ? "s" : ""}`}
                    </span>
                  </Form.Field>
                </Form.Group>
              </Form>
            </section>

            <section className="registros-inpi-tabela-wrap">
              <Table celled className="registros-inpi-tabela">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Cliente</Table.HeaderCell>
                    <Table.HeaderCell>Registro</Table.HeaderCell>
                    <Table.HeaderCell>Protocolo</Table.HeaderCell>
                    <Table.HeaderCell>Tipo</Table.HeaderCell>
                    <Table.HeaderCell>Nº Serviço</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Última movimentação</Table.HeaderCell>
                    <Table.HeaderCell></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {loading ? (
                    <Table.Row>
                      <Table.Cell colSpan="8" className="registros-inpi-empty">
                        <p>Carregando...</p>
                      </Table.Cell>
                    </Table.Row>
                  ) : error ? (
                    <Table.Row>
                      <Table.Cell colSpan="8" className="registros-inpi-empty">
                        <p>{error}</p>
                      </Table.Cell>
                    </Table.Row>
                  ) : registros.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan="8" className="registros-inpi-empty">
                        <FaFileAlt size={36} />
                        <p>Nenhum registro encontrado</p>
                        <span>Ajuste os filtros ou adicione um novo pedido</span>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    registros.map((registro) => (
                      <Table.Row
                        key={registro.id}
                        className="registros-inpi-row"
                        onClick={() => {
                          setSelectedRegistro(registro);
                          setDialogDetalheAberto(true);
                        }}
                      >
                        <Table.Cell>
                          <div className="registros-inpi-cell-cliente">
                            <ClienteAvatar nome={registro.cliente} />
                            <span>{registro.cliente}</span>
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <p className="registros-inpi-nome">{registro.nome}</p>
                          <p className="registros-inpi-data">Aberto em {registro.dataAbertura}</p>
                        </Table.Cell>
                        <Table.Cell>
                          <code className="registros-inpi-protocolo">{registro.protocolo}</code>
                        </Table.Cell>
                        <Table.Cell>
                          <TipoIcon tipo={registro.tipo} showLabel />
                        </Table.Cell>
                        <Table.Cell>
                          {registro.numeroServico ? (
                            <a href={`#servico/${registro.numeroServico}`} className="registros-inpi-link" onClick={(e) => e.stopPropagation()}>
                              <FaExternalLinkAlt /> {registro.numeroServico}
                            </a>
                          ) : (
                            <span className="registros-inpi-nao">—</span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <StatusBadge status={registro.status} />
                        </Table.Cell>
                        <Table.Cell className="registros-inpi-cell-ultima-mov">
                          <Popup
                            trigger={
                              <span className="registros-inpi-mov" title={registro.ultimaMovimentacao}>
                                <FaClock />
                                <span className="registros-inpi-mov-text">{truncateMovimentacao(registro.ultimaMovimentacao)}</span>
                              </span>
                            }
                            content={
                              <div className="registros-inpi-popup registros-inpi-popup-mov">
                                {registro.ultimaMovimentacao}
                              </div>
                            }
                            on="hover"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <FaChevronRight className="registros-inpi-chevron" />
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
              {lastPage > 1 && (
                <div className="registros-inpi-pagination" style={{ marginTop: "1rem", display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    disabled={currentPage <= 1 || loading}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Anterior
                  </Button>
                  <span style={{ fontSize: "0.9rem" }}>
                    Página {currentPage} de {lastPage}
                  </span>
                  <Button
                    size="small"
                    disabled={currentPage >= lastPage || loading}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      <Modal open={dialogCadastrarAberto} onClose={() => !submitLoading && setDialogCadastrarAberto(false)} size="small">
        <Modal.Header>Cadastrar protocolo existente no INPI</Modal.Header>
        <Modal.Content>
          <p>Informe o cliente, o número do processo e o tipo. O processo será consultado no INPI e vinculado ao cliente.</p>
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600 }}>Cliente (obrigatório)</label>
            <Dropdown
              placeholder="Digite para buscar o cliente..."
              fluid
              search
              selection
              clearable
              value={formClientId}
              options={
                formClientId && formClientLabel
                  ? [
                      { key: formClientId, value: formClientId, text: formClientLabel },
                      ...clientOptions.filter((o) => o.value !== formClientId),
                    ]
                  : clientOptions
              }
              onSearchChange={handleClientSearchChange}
              onOpen={handleClientDropdownOpen}
              onChange={handleClientChange}
              loading={clientSearchLoading}
              searchQuery={clientSearchQuery}
              noResultsMessage={clientSearchLoading ? "Buscando..." : "Digite para buscar ou não há clientes."}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600 }}>Número do processo (obrigatório)</label>
            <input
              type="text"
              placeholder="Ex: 123456789"
              value={formProcessNumber}
              onChange={(e) => setFormProcessNumber(e.target.value)}
              style={{ width: "100%", padding: "8px", border: "1px solid #999", borderRadius: "4px" }}
            />
                  </div>
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", marginBottom: "0.25rem", fontWeight: 600 }}>Tipo do processo (obrigatório)</label>
            <Select
              options={[
                { key: "marca", value: "marca", text: "Marca" },
                { key: "patente", value: "patente", text: "Patente" },
                { key: "software", value: "software", text: "Registro de Software" },
                { key: "marca_internacional", value: "marca_internacional", text: "Marca Internacional" },
              ]}
              value={formProcessType}
              onChange={(e, { value }) => setFormProcessType(value)}
              style={{ width: "100%" }}
            />
              </div>
          {submitError && (
            <p style={{ color: "#c00", marginBottom: "1rem" }}>{submitError}</p>
          )}
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
            <Button onClick={() => !submitLoading && setDialogCadastrarAberto(false)} disabled={submitLoading}>
              Cancelar
            </Button>
            <Button primary onClick={handleSubmitCadastrar} loading={submitLoading} disabled={submitLoading}>
              Cadastrar
            </Button>
          </div>
        </Modal.Content>
      </Modal>

      <Modal open={dialogDetalheAberto} onClose={() => setDialogDetalheAberto(false)} size="large" className="registros-inpi-modal-detalhe-wrap">
        {selectedRegistro && (
          <>
            <Modal.Header>
              <div className="registros-inpi-detalhe-header">
                <TipoIcon tipo={selectedRegistro.tipo} />
                <span>{selectedRegistro.nome}</span>
              </div>
            </Modal.Header>
            <Modal.Content>
              <div className="registros-inpi-modal-detalhe">
                {(() => {
                  const proc = processDetail;
                  const tipo = selectedRegistro.tipo || proc?.processType || "marca";
                  const isSoftware = tipo === "software";
                  const isPatente = tipo === "patente";
                  const rp = proc?.renewalPeriods;
                  const classes = proc?.classes || (Array.isArray(proc?.classNumbers) ? proc.classNumbers.map((c) => (typeof c === "object" ? c : { classeNice: String(c), situacao: "—", especificacao: "—" })) : []);
                  const petitions = proc?.petitions || [];
                  const movements = proc?.movements || [];
                  const hasClasses = classes.length > 0;
                  const hasPetitions = petitions.length > 0;
                  const hasMovements = movements.length > 0;
                  const protocoloFormatado = formatProcessNumberBR(selectedRegistro.protocolo) || selectedRegistro.protocolo;

                  const renderPublicacoes = () => (
                    <div className="inpi-detalhe-section inpi-publicacoes-section">
                      <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection("publicacoes")}>
                        <h4 className="inpi-detalhe-section-title">
                          <span className="inpi-section-icon"><FaList size={18} /></span>
                          Publicações
                          {hasMovements && <span className="inpi-section-badge">{movements.length}</span>}
                        </h4>
                        {detalheSections.publicacoes ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                      </button>
                      {detalheSections.publicacoes && (
                        <>
                          {loadingDetail ? (
                            <div className="inpi-publicacoes-loading">
                              <div className="inpi-loading-dots" />
                              <span>Carregando publicações...</span>
                            </div>
                          ) : hasMovements ? (
                            <div className="inpi-publicacoes-timeline">
                              <div className="inpi-timeline-track" />
                              {movements.map((mov, idx) => (
                                <article key={mov._id || idx} className={`inpi-publicacao-card ${idx === 0 ? "inpi-publicacao-recente" : ""}`}>
                                  <div className="inpi-publicacao-marker">
                                    <span className="inpi-publicacao-dot" />
                                  </div>
                                  <div className="inpi-publicacao-body">
                                    <header className="inpi-publicacao-header">
                                      <div className="inpi-publicacao-meta">
                                        {idx === 0 && <span className="inpi-publicacao-recente-badge">Mais recente</span>}
                                        {(mov.rpi || mov.code) && <span className="inpi-publicacao-rpi">RPI {mov.rpi || mov.code}</span>}
                                        <time className="inpi-publicacao-data">{formatDateBR(mov.date) || "—"}</time>
                                      </div>
                                    </header>
                                    <p className="inpi-publicacao-descricao">{mov.description || "—"}</p>
                                    {mov.complemento && (
                                      <details className="inpi-publicacao-complemento">
                                        <summary>
                                          <span>Ver detalhes do despacho</span>
                                          <FaChevronRight size={14} className="inpi-complemento-chevron" />
                                        </summary>
                                        <div className="inpi-publicacao-complemento-content">
                                          {formatComplemento(mov.complemento).split("\n").filter(Boolean).map((line, i) => (
                                            <span key={i} className="inpi-complemento-line">{line}</span>
                                          ))}
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                </article>
                              ))}
                            </div>
                          ) : (
                            <div className="inpi-publicacoes-empty">
                              <FaList size={40} />
                              <p>Nenhuma publicação registrada</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );

                  if (isSoftware) {
                    const softwarePublicationDate = proc?.publicationDate || (movements[0] && movements[0].date);
                    const campoAplicacaoItems = Array.isArray(proc?.campoAplicacao) ? proc.campoAplicacao : (proc?.campoAplicacao ? [proc.campoAplicacao] : []);
                    const tipoProgramaItems = Array.isArray(proc?.tipoPrograma) ? proc.tipoPrograma : (proc?.tipoPrograma ? [proc.tipoPrograma] : []);
                    return (
                      <div className="inpi-detalhe-scroll">
                        <div className="inpi-detalhe-header-row">
                          <TipoIcon tipo={selectedRegistro.tipo} />
                          <StatusBadge status={selectedRegistro.status} />
                          <code className="inpi-detalhe-value inpi-protocolo">{protocoloFormatado}</code>
                        </div>
                        <div className="inpi-detalhe-section">
                          <h4 className="inpi-detalhe-section-title">Programa de Computador</h4>
                          <div className="inpi-detalhe-grid">
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Nº do Pedido</p>
                              <p className="inpi-detalhe-value">{protocoloFormatado}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Título</p>
                              <p className="inpi-detalhe-value">{proc?.trademarkName || selectedRegistro.nome || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Data do Depósito</p>
                              <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Situação</p>
                              <p className="inpi-detalhe-value">{proc?.status || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field inpi-detalhe-field-full">
                              <p className="inpi-detalhe-label">Linguagem</p>
                              <p className="inpi-detalhe-value">{proc?.linguagem || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field inpi-detalhe-field-full">
                              <p className="inpi-detalhe-label">Campo de Aplicação</p>
                              <div className="inpi-detalhe-value inpi-lines-wrap">
                                {campoAplicacaoItems.length ? campoAplicacaoItems.map((line, i) => <div key={i} className="inpi-line-item">{line}</div>) : "—"}
                              </div>
                            </div>
                            <div className="inpi-detalhe-field inpi-detalhe-field-full">
                              <p className="inpi-detalhe-label">Tipo Programa</p>
                              <div className="inpi-detalhe-value inpi-lines-wrap">
                                {tipoProgramaItems.length ? tipoProgramaItems.map((line, i) => <div key={i} className="inpi-line-item">{line}</div>) : "—"}
                              </div>
                            </div>
                            <div className="inpi-detalhe-field inpi-detalhe-field-full">
                              <p className="inpi-detalhe-label">Despacho / Observações</p>
                              <p className="inpi-detalhe-value">{proc?.statusDetail || "—"}</p>
                            </div>
                          </div>
                        </div>
                        <div className="inpi-detalhe-section">
                          <h4 className="inpi-detalhe-section-title">Titular, Autor e Procurador</h4>
                          <div className="inpi-detalhe-grid">
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Nome do Titular</p>
                              <p className="inpi-detalhe-value">{(!proc?.applicant || /^Titular\(\d+\):?\s*$/i.test(proc.applicant)) ? "—" : proc.applicant}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Nome do Autor</p>
                              <p className="inpi-detalhe-value">{proc?.autor || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Nome do Procurador</p>
                              <p className="inpi-detalhe-value">{(!proc?.procurador || /^Procurador:?\s*$/i.test(proc.procurador)) ? "—" : proc.procurador}</p>
                            </div>
                          </div>
                        </div>
                        <div className="inpi-detalhe-section">
                          <h4 className="inpi-detalhe-section-title">Datas</h4>
                          <div className="inpi-detalhe-grid inpi-detalhe-grid-3">
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Data do Depósito</p>
                              <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Data de Publicação (RPI)</p>
                              <p className="inpi-detalhe-value">{formatDateBR(softwarePublicationDate) || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Última atualização</p>
                              <p className="inpi-detalhe-value">
                                {proc?.lastScrapedAt ? formatDateBR(typeof proc.lastScrapedAt === "number" ? new Date(proc.lastScrapedAt).toISOString().slice(0, 10) : proc.lastScrapedAt) : "—"}
                              </p>
                            </div>
                          </div>
                        </div>
                        {hasPetitions && (
                          <div className="inpi-detalhe-section">
                            <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection("peticoes")}>
                              <h4 className="inpi-detalhe-section-title">
                                <span className="inpi-section-icon"><FaFileAlt size={18} /></span>
                                Petições
                                <span className="inpi-section-badge">{petitions.length}</span>
                              </h4>
                              {detalheSections.peticoes ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                            </button>
                            {detalheSections.peticoes && (
                              <div className="inpi-detalhe-table-wrap">
                                <table className="inpi-detalhe-table">
                                  <thead>
                                    <tr>
                                      <th>Pagamento</th>
                                      <th>Protocolo</th>
                                      <th>Data</th>
                                      <th>Serviço</th>
                                      <th>Cliente</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {petitions.map((p, i) => (
                                      <tr key={i}>
                                        <td>
                                          {p.statusDescricao || p.status ? (
                                            <Popup trigger={<span className="inpi-servico-cell inpi-status-hoverable" title="Informações de pagamento">{p.status || (p.statusDescricao ? "Ver pagamento" : "—")}</span>} on="hover" position="top center" content={<p className="inpi-servico-descricao-popover">{p.statusDescricao || p.status || "—"}</p>} />
                                          ) : <span>—</span>}
                                        </td>
                                        <td><code className="inpi-table-code">{p.protocolo || "—"}</code></td>
                                        <td>{formatDateBR(p.data) || "—"}</td>
                                        <td>
                                          {p.servicoDescricao ? (
                                            <Popup trigger={<span className="inpi-servico-cell"><code className="inpi-table-code">{p.servico || "—"}</code></span>} on="hover" position="top center" content={<p className="inpi-servico-descricao-popover">{p.servicoDescricao}</p>} />
                                          ) : <code className="inpi-table-code">{p.servico || "—"}</code>}
                                        </td>
                                        <td>{p.cliente || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                        {renderPublicacoes()}
                        {proc?.lastUpdateInfo && <p className="inpi-detalhe-footer">{proc.lastUpdateInfo}</p>}
                      </div>
                    );
                  }

                  if (isPatente) {
                    const patentePublicationDate = proc?.publicationDate || (movements[0] && movements[0].date);
                    return (
                      <div className="inpi-detalhe-scroll">
                        <div className="inpi-detalhe-header-row">
                          <TipoIcon tipo={selectedRegistro.tipo} />
                          <StatusBadge status={selectedRegistro.status} />
                          <code className="inpi-detalhe-value inpi-protocolo">{protocoloFormatado}</code>
                        </div>
                        <div className="inpi-detalhe-section">
                          <h4 className="inpi-detalhe-section-title">Patente</h4>
                          <div className="inpi-detalhe-grid">
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Nº do Pedido</p>
                              <p className="inpi-detalhe-value">{protocoloFormatado}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Título</p>
                              <p className="inpi-detalhe-value">{proc?.trademarkName || proc?.title || selectedRegistro.nome || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Data do Depósito</p>
                              <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Situação</p>
                              <p className="inpi-detalhe-value">{proc?.status || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field inpi-detalhe-field-full">
                              <p className="inpi-detalhe-label">Despacho / Observações</p>
                              <p className="inpi-detalhe-value">{proc?.statusDetail || "—"}</p>
                            </div>
                          </div>
                        </div>
                        <div className="inpi-detalhe-section">
                          <h4 className="inpi-detalhe-section-title">Depositante e Procurador</h4>
                          <div className="inpi-detalhe-grid">
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Nome do Depositante</p>
                              <p className="inpi-detalhe-value">{proc?.applicant || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Nome do Procurador</p>
                              <p className="inpi-detalhe-value">{proc?.procurador || "—"}</p>
                            </div>
                          </div>
                        </div>
                        <div className="inpi-detalhe-section">
                          <h4 className="inpi-detalhe-section-title">Datas</h4>
                          <div className="inpi-detalhe-grid inpi-detalhe-grid-3">
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Data do Depósito</p>
                              <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Data da Publicação</p>
                              <p className="inpi-detalhe-value">{formatDateBR(patentePublicationDate) || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Data da Concessão</p>
                              <p className="inpi-detalhe-value">{formatDateBR(proc?.grantDate) || "—"}</p>
                            </div>
                            <div className="inpi-detalhe-field">
                              <p className="inpi-detalhe-label">Última atualização</p>
                              <p className="inpi-detalhe-value">
                                {proc?.lastScrapedAt ? formatDateBR(typeof proc.lastScrapedAt === "number" ? new Date(proc.lastScrapedAt).toISOString().slice(0, 10) : String(proc.lastScrapedAt).slice(0, 10)) : "—"}
                              </p>
                            </div>
                          </div>
                        </div>
                        {hasPetitions && (
                          <div className="inpi-detalhe-section">
                            <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection("peticoes")}>
                              <h4 className="inpi-detalhe-section-title">
                                <span className="inpi-section-icon"><FaFileAlt size={18} /></span>
                                Petições
                                <span className="inpi-section-badge">{petitions.length}</span>
                              </h4>
                              {detalheSections.peticoes ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                            </button>
                            {detalheSections.peticoes && (
                              <div className="inpi-detalhe-table-wrap">
                                <table className="inpi-detalhe-table">
                                  <thead>
                                    <tr>
                                      <th>Pagamento</th>
                                      <th>Protocolo</th>
                                      <th>Data</th>
                                      <th>Serviço</th>
                                      <th>Cliente</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {petitions.map((p, i) => (
                                      <tr key={i}>
                                        <td>
                                          {p.statusDescricao || p.status ? (
                                            <Popup trigger={<span className="inpi-servico-cell inpi-status-hoverable">{p.status || (p.statusDescricao ? "Ver pagamento" : "—")}</span>} on="hover" position="top center" content={<p className="inpi-servico-descricao-popover">{p.statusDescricao || p.status || "—"}</p>} />
                                          ) : <span>—</span>}
                                        </td>
                                        <td><code className="inpi-table-code">{p.protocolo || "—"}</code></td>
                                        <td>{formatDateBR(p.data) || "—"}</td>
                                        <td>
                                          {p.servicoDescricao ? (
                                            <Popup trigger={<span className="inpi-servico-cell"><code className="inpi-table-code">{p.servico || "—"}</code></span>} on="hover" position="top center" content={<p className="inpi-servico-descricao-popover">{p.servicoDescricao}</p>} />
                                          ) : <code className="inpi-table-code">{p.servico || "—"}</code>}
                                        </td>
                                        <td>{p.cliente || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                        {renderPublicacoes()}
                        {proc?.lastUpdateInfo && <p className="inpi-detalhe-footer">{proc.lastUpdateInfo}</p>}
                      </div>
                    );
                  }

                  return (
                    <div className="inpi-detalhe-scroll">
                      <div className="inpi-detalhe-header-row">
                        <TipoIcon tipo={selectedRegistro.tipo} />
                <StatusBadge status={selectedRegistro.status} />
                        <code className="inpi-detalhe-value inpi-protocolo">{selectedRegistro.protocolo}</code>
                      </div>
                      <div className="inpi-detalhe-section">
                        <h4 className="inpi-detalhe-section-title">Resumo</h4>
                        <div className="inpi-detalhe-grid">
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Nº do Processo</p>
                            <p className="inpi-detalhe-value">{selectedRegistro.protocolo}</p>
                          </div>
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Marca</p>
                            <p className="inpi-detalhe-value">{proc?.trademarkName || selectedRegistro.nome || "—"}</p>
                          </div>
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Situação</p>
                            <p className="inpi-detalhe-value">{proc?.status || "—"}</p>
                          </div>
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Apresentação</p>
                            <p className="inpi-detalhe-value">{proc?.apresentacao || "—"}</p>
                          </div>
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Natureza</p>
                            <p className="inpi-detalhe-value">{proc?.natureza || "—"}</p>
                          </div>
                        </div>
                      </div>
                      <div className="inpi-detalhe-section">
                        <h4 className="inpi-detalhe-section-title">Titulares e Representante Legal</h4>
                        <div className="inpi-detalhe-grid">
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Titular</p>
                            <p className="inpi-detalhe-value">{(!proc?.applicant || /^Titular\(\d+\):?\s*$/i.test(proc.applicant)) ? "—" : proc.applicant}</p>
                          </div>
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Procurador</p>
                            <p className="inpi-detalhe-value">{(!proc?.procurador || /^Procurador:?\s*$/i.test(proc.procurador)) ? "—" : proc.procurador}</p>
                          </div>
                        </div>
                      </div>
                      <div className="inpi-detalhe-section">
                        <h4 className="inpi-detalhe-section-title">Datas</h4>
                        <div className="inpi-detalhe-grid inpi-detalhe-grid-3">
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Data de Depósito</p>
                            <p className="inpi-detalhe-value">{formatDateBR(proc?.depositDate) || selectedRegistro.dataAbertura || "—"}</p>
                          </div>
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Data de Concessão</p>
                            <p className="inpi-detalhe-value">{formatDateBR(proc?.grantDate) || formatDateBR(proc?.publicationDate) || "—"}</p>
                          </div>
                          <div className="inpi-detalhe-field">
                            <p className="inpi-detalhe-label">Data de Vigência</p>
                            <p className="inpi-detalhe-value">{formatDateBR(proc?.vigenciaDate) || "—"}</p>
                          </div>
                        </div>
                      </div>
                      {rp && (rp.ordinary?.start || rp.ordinary?.end || rp.extraordinary?.start || rp.extraordinary?.end) && (
                        <div className="inpi-detalhe-section">
                          <h4 className="inpi-detalhe-section-title">Prazos para prorrogação de registro</h4>
                          <div className="inpi-detalhe-table-wrap">
                            <table className="inpi-detalhe-table">
                              <thead>
                                <tr>
                                  <th />
                                  <th>Prazo Ordinário</th>
                                  <th>Prazo Extraordinário</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td><strong>Início</strong></td>
                                  <td>{formatDateBR(rp.ordinary?.start) || "—"}</td>
                                  <td>{formatDateBR(rp.extraordinary?.start) || "—"}</td>
                                </tr>
                                <tr>
                                  <td><strong>Fim</strong></td>
                                  <td>{formatDateBR(rp.ordinary?.end) || "—"}</td>
                                  <td>{formatDateBR(rp.extraordinary?.end) || "—"}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {hasClasses && (
                        <div className="inpi-detalhe-section">
                          <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection("classificacao")}>
                            <h4 className="inpi-detalhe-section-title">
                              <span className="inpi-section-icon"><FaFileAlt size={18} /></span>
                              Classificação de Produtos / Serviços
                              <span className="inpi-section-badge">{classes.length}</span>
                            </h4>
                            {detalheSections.classificacao ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                          </button>
                          {detalheSections.classificacao && (
                            <div className="inpi-detalhe-table-wrap">
                              <table className="inpi-detalhe-table">
                                <thead>
                                  <tr>
                                    <th>Classe de Nice</th>
                                    <th>Situação da Classe</th>
                                    <th>Especificação</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {classes.map((c, i) => (
                                    <tr key={i}>
                                      <td>{c.classeNice || "—"}</td>
                                      <td>{c.situacao || "—"}</td>
                                      <td>
                                        {c.especificacao && c.especificacao.length > 80 ? (
                                          <Popup trigger={<span className="inpi-especificacao-cell">{c.especificacao.substring(0, 80)}...</span>} on="hover" position="left center" content={<p className="inpi-especificacao-popover-text">{c.especificacao}</p>} />
                                        ) : (c.especificacao || "—")}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
              </div>
                          )}
              </div>
                      )}
                      {hasPetitions && (
                        <div className="inpi-detalhe-section">
                          <button type="button" className="inpi-detalhe-section-toggle" onClick={() => toggleDetalheSection("peticoes")}>
                            <h4 className="inpi-detalhe-section-title">
                              <span className="inpi-section-icon"><FaFileAlt size={18} /></span>
                              Petições
                              <span className="inpi-section-badge">{petitions.length}</span>
                            </h4>
                            {detalheSections.peticoes ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                          </button>
                          {detalheSections.peticoes && (
                            <div className="inpi-detalhe-table-wrap">
                              <table className="inpi-detalhe-table">
                                <thead>
                                  <tr>
                                    <th>Protocolo</th>
                                    <th>Data</th>
                                    <th>Serviço</th>
                                    <th>Cliente</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {petitions.map((p, i) => (
                                    <tr key={i}>
                                      <td><code className="inpi-table-code">{p.protocolo || "—"}</code></td>
                                      <td>{formatDateBR(p.data) || "—"}</td>
                                      <td>
                                        {p.servicoDescricao ? (
                                          <Popup trigger={<span className="inpi-servico-cell"><code className="inpi-table-code">{p.servico || "—"}</code></span>} on="hover" position="top center" content={<p className="inpi-servico-descricao-popover">{p.servicoDescricao}</p>} />
                                        ) : <code className="inpi-table-code">{p.servico || "—"}</code>}
                                      </td>
                                      <td>{p.cliente || "—"}</td>
                                      <td>
                                        {p.statusDescricao ? (
                                          <Popup trigger={<span className="inpi-servico-cell inpi-status-hoverable" title="Passe o mouse para ver detalhes do pagamento">{p.status || "—"}</span>} on="hover" position="top center" content={<p className="inpi-servico-descricao-popover">{p.statusDescricao}</p>} />
                                        ) : (p.status || "—")}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
              </div>
                          )}
              </div>
                      )}
                      {renderPublicacoes()}
                      {proc?.lastUpdateInfo && <p className="inpi-detalhe-footer">{proc.lastUpdateInfo}</p>}
              </div>
                  );
                })()}
                <div className="inpi-detalhe-actions">
                  <button
                    type="button"
                    className="inpi-detalhe-btn-apagar"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApagarProcesso();
                    }}
                    disabled={deleteLoading}
                  >
                    <FaTrash size={14} />
                    {deleteLoading ? "Removendo..." : "Apagar processo"}
                  </button>
                  <button
                    type="button"
                    className="inpi-detalhe-btn-fechar"
                    onClick={() => setDialogDetalheAberto(false)}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </Modal.Content>
          </>
        )}
      </Modal>
    </div>
  );
};

export default RegistrosINPI;
